import { Dropdown } from 'bootstrap'
import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  useMarkRead,
  useNotificationsQuery,
  useUnreadCount,
} from '@/features/notifications/hooks/useNotifications'
import { formatRelativeTime } from '@/utils/format'

const BellIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" className="header-link-icon" viewBox="0 0 256 256">
    <rect width="256" height="256" fill="none" />
    <path
      d="M96,192a32,32,0,0,0,64,0"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <path
      d="M56,112a72,72,0,0,1,144,0c0,35.82,8.3,56.6,14.9,68A8,8,0,0,1,208,192H48a8,8,0,0,1-6.88-12C47.71,168.6,56,147.81,56,112Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
)

export function NotificationBell() {
  const navigate = useNavigate()
  const { data: unreadCount } = useUnreadCount()
  const { data } = useNotificationsQuery(5)
  const markRead = useMarkRead()

  const toggleRef = useRef<HTMLButtonElement>(null)
  const dropdownInstanceRef = useRef<Dropdown | null>(null)

  // Same reason as AppHeader.tsx's profile dropdown: data-bs-toggle's
  // document-level click delegation doesn't reliably fire in this app, so
  // the dropdown is driven explicitly via the instance's own .toggle().
  useEffect(() => {
    const el = toggleRef.current
    if (!el) return

    const instance = new Dropdown(el)
    dropdownInstanceRef.current = instance

    return () => {
      instance.dispose()
      dropdownInstanceRef.current = null
    }
  }, [])

  const notifications = data?.notifications ?? []

  const handleClick = (id: string, link: string | null, readAt: string | null) => {
    if (!readAt) markRead.mutate(id)
    if (link) void navigate(link)
  }

  return (
    <li className="header-element notifications-dropdown dropdown">
      <button
        ref={toggleRef}
        type="button"
        className={`header-link dropdown-toggle btn btn-link position-relative ${!!unreadCount && unreadCount > 0 ? 'animate-bell' : ''}`.trim()}
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
        onClick={() => dropdownInstanceRef.current?.toggle()}
      >
        {BellIcon}
        {!!unreadCount && unreadCount > 0 && (
          <span
            className="badge bg-danger rounded-pill position-absolute"
            style={{ top: 4, right: 4, fontSize: 10 }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      <div
        className="main-header-dropdown dropdown-menu dropdown-menu-end"
        data-popper-placement="none"
      >
        <div className="p-3">
          <p className="mb-0 fs-16">Notifications</p>
        </div>
        <div className="dropdown-divider" />
        {notifications.length === 0 ? (
          <div className="p-5 text-center">
            <span className="avatar avatar-xl avatar-rounded bg-secondary-transparent">
              <i className="ri-notification-off-line fs-2" />
            </span>
            <h6 className="fw-medium mt-3">No New Notifications</h6>
          </div>
        ) : (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {notifications.map((n) => (
              <button
                key={n.id}
                type="button"
                className={`dropdown-item text-wrap py-2 ${n.readAt ? '' : 'bg-primary-transparent'}`.trim()}
                onClick={() => handleClick(n.id, n.link, n.readAt)}
              >
                <p className="fw-medium mb-0 fs-13">{n.title}</p>
                {n.body && <p className="fs-12 text-muted mb-0">{n.body}</p>}
                <p className="fs-11 text-muted mb-0">{formatRelativeTime(n.createdAt)}</p>
              </button>
            ))}
          </div>
        )}
        <div className="dropdown-divider" />
        <div className="p-2 text-center">
          <Link to="/notifications" className="btn btn-sm btn-link">
            View all
          </Link>
        </div>
      </div>
    </li>
  )
}
